import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home } from 'lucide-react';
import AddressSearchInput from '../components/AddressSearchInput';
import RecentReviews from '../components/RecentReviews';
import { trackPageView } from '../utils/analytics';

export default function LeaveReviewPage() {
  const navigate = useNavigate();
  
  // Track page view on component mount
  React.useEffect(() => {
    trackPageView('Leave Review Page', window.location.href);
  }, []);
  
  const handleSelectAddress = (selectedProperty: any) => {
    navigate(`/property/${encodeURIComponent(selectedProperty.fullAddress)}?udprn=${selectedProperty.udprn}`);
  };

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div 
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-50"
        style={{
          backgroundImage: 'url("https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg")'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/80 via-black/60 to-black/40" />
      <header className="relative z-10">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img
                src="https://rentreview.io/wp-content/uploads/2025/05/Rent-Review-Logo.png"
                alt="RentReview Logo"
                className="h-12"
              />
            </Link>
          </div>
          <a
            href="https://www.instagram.com/rentreviewsuk"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-3 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium text-xs sm:text-sm hover:shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span className="hidden xs:inline sm:inline">Follow us on Instagram</span>
            <span className="xs:hidden sm:hidden">Follow</span>
          </a>
        </nav>
      </header>

      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <div className="flex flex-col items-center justify-center space-y-12 md:space-y-20">
          <div className="text-center space-y-4 md:space-y-6">
            <h1 className="text-4xl md:text-7xl font-bold leading-tight">
              <span className="bg-[#30a86b] text-black px-3 md:px-6 py-2 rounded-full inline-block">Share Your</span> Story,<br />
              Help the Next Renter
            </h1>
            <div className="flex items-center justify-center gap-2 text-lg md:text-xl">
              <span className="inline-block">💬</span> By Renters. For Renters
            </div>
          </div>

          <AddressSearchInput
            placeholder="Search for a property to leave or find a review..."
            onSelectAddress={handleSelectAddress}
          />

          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-[#edae6d] rounded-2xl p-6 md:p-8 md:pr-4 md:pt-16 order-1 md:order-1">
              <h2 className="text-black text-5xl md:text-6xl font-extrabold mb-8 md:mb-6">
                Changing<br />
                Renting<br />
                One Review<br />
                at a Time
              </h2>
              <div className="space-y-3 md:space-y-4 mt-8 md:mt-8">
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/19.png" alt="Damp" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Damp and Mould</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/20.png" alt="Deposit" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Unreturned Deposits</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/21.png" alt="Ghost" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Ghosting Letting Agents</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/22.png" alt="Rat" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Rodents and Pests</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/24.png" alt="Boiler" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Broken Boilers in Winter</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/23.png" alt="Unlivable" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Unlivable Conditions</span>
                </div>
                <div className="flex items-center gap-2.5 md:gap-3 text-black">
                  <img src="https://rentreview.io/wp-content/uploads/2025/05/25.png" alt="Noise" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                  <span className="text-base md:text-lg font-bold">Noisy Neighbours or Party Flats</span>
                </div>
              </div>
            </div>

            <div className="space-y-4 order-2 md:order-2">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center">Browse Recent Reviews</h2>
              <div className="text-gray-900">
                <RecentReviews />
              </div>
            </div>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}